import { memo, useMemo } from "react";

import { getZcCollateral, getZcLedger, useWhirlpoolDecreaseLiquidityTransaction } from "@bridgesplit/abf-react";
import { decimalsToBps, LOADING_ERROR, Result } from "@bridgesplit/utils";
import { GetUpdateLiquidityTxnArgs } from "@bridgesplit/abf-sdk";

import { AppButton } from "../../../../common";
import { useWhirlpoolContext } from "../WhirlpoolContext";
import { useTransactionSender } from "../../../../transactions";
import { useWithdrawLtv } from "./util";

export default memo(function WithdrawCta() {
    const { withdrawForm, poolData, withdrawQuote } = useWhirlpoolContext();
    const removeLiquidity = useRemoveLiquidity();
    const withdrawLtv = useWithdrawLtv();

    const ctaError = useMemo(() => {
        if (!withdrawForm.percentToWithdraw) return "Enter an amount";
        if (withdrawForm.percentToWithdraw > 1) return "Invalid withdraw amount";
        if (!poolData || !withdrawQuote) return "Loading";

        if (withdrawForm.status === "error") return "No quote found";
        if (withdrawLtv.insufficientCollateral) return "Insufficient collateral";

        return undefined;
    }, [
        withdrawForm.percentToWithdraw,
        withdrawForm.status,
        poolData,
        withdrawQuote,
        withdrawLtv.insufficientCollateral
    ]);

    return (
        <AppButton
            asyncCta={{ onClickWithResult: removeLiquidity }}
            isTransaction
            color="secondary"
            disabled={!!ctaError}
        >
            {ctaError ?? "Withdraw"}
        </AppButton>
    );
});

function useRemoveLiquidity() {
    const {
        withdrawQuote,
        loanExpanded,
        reset,
        slippageController: { isAutoSlippage }
    } = useWhirlpoolContext();
    const send = useTransactionSender();
    const removeLiquidity = useWhirlpoolDecreaseLiquidityTransaction();

    return async () => {
        const zcLedger = getZcLedger(loanExpanded);
        const positionMint = getZcCollateral(loanExpanded)?.assetMint;
        if (!withdrawQuote || !zcLedger || !positionMint) return Result.errFromMessage(LOADING_ERROR);
        const params: GetUpdateLiquidityTxnArgs = {
            loan: loanExpanded.loan.address,
            increase: false,
            positionMint,
            manageParams: {
                collateralIndex: 0,
                liquidityAmount: withdrawQuote.liquidity.toNumber(),
                transferParams: isAutoSlippage
                    ? { Liquidity: { slippageToleranceBps: decimalsToBps(0.1, "bps") } }
                    : {
                          TokenAmounts: {
                              tokenAAmount: withdrawQuote.minTokenA.toNumber(),
                              tokenBAmount: withdrawQuote.minTokenB.toNumber()
                          }
                      }
            }
        };
        return await send(removeLiquidity, params, { sendOptions: { onSuccess: reset }, preventAutoDialogClose: true });
    };
}
